/**
 * Step 2: guest count collection, plus head-table configuration for
 * reception layouts.
 *
 * The head-table section is hidden when the coordinator is setting up a
 * ceremony (startCeremony) or a blank layout (startBlank), since neither
 * path uses a head table. For those paths this step simply asks for the
 * expected guest count (used as the default chair count for ceremony).
 *
 * For reception layouts this step is unchanged from the original — both
 * questions appear together per the original design rationale: "ask it
 * up front gets you a better first-draft layout; one combined step in
 * onboarding, not two separate questions."
 */

import type { WizardData } from './NewWeddingWizard';

interface StepGuestCountProps {
  data: WizardData;
  onChange: (changes: Partial<WizardData>) => void;
  onNext: () => void;
}

export function StepGuestCount({ data, onChange, onNext }: StepGuestCountProps) {
  const canContinue = data.guestCount > 0;

  // Head-table questions are only relevant for reception layouts.
  // Ceremony and blank don't have a head table, and asking about it
  // would be confusing.
  const showHeadTableSection = !data.startBlank && !data.startCeremony;

  function handleGuestCountChange(raw: string) {
    const n = Number(raw);
    onChange({
      guestCount: n,
      // Keep ceremonyChairCount in sync with guestCount so the ceremony step
      // shows a sensible default. If the coordinator has already explicitly
      // adjusted ceremonyChairCount, that adjustment is preserved because
      // StepStyleSelect sets it independently — this sync only matters when
      // the user hasn't visited the ceremony sub-form yet.
      ceremonyChairCount: n,
    });
  }

  return (
    <div>
      <h2 className="text-center font-display text-2xl text-ink-700">
        How many guests are you expecting?
      </h2>

      <div className="mt-6 flex justify-center">
        <input
          type="number"
          min={1}
          value={data.guestCount}
          onChange={(e) => handleGuestCountChange(e.target.value)}
          className="w-32 rounded border border-ivory-300 bg-white px-4 py-3 text-center font-display text-3xl text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
        />
      </div>

      {showHeadTableSection && (
        <>
          <h3 className="mt-8 text-center font-display text-lg text-ink-600">
            How many at your head table?
          </h3>

          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-3 rounded border border-ivory-300 bg-white px-4 py-3">
              <input
                type="radio"
                name="head-table-type"
                checked={data.headTableType === 'head_table'}
                onChange={() => onChange({ headTableType: 'head_table' })}
              />
              <span className="font-body text-sm text-ink-600">Head table, party of:</span>
              <input
                type="number"
                min={1}
                value={data.headTablePartySize ?? 6}
                disabled={data.headTableType !== 'head_table'}
                onChange={(e) => onChange({ headTablePartySize: Number(e.target.value) })}
                className="ml-auto w-16 rounded border border-ivory-300 bg-white px-2 py-1 text-center font-body text-sm text-ink-700 disabled:bg-ivory-100"
              />
            </label>

            <label className="flex items-center gap-3 rounded border border-ivory-300 bg-white px-4 py-3">
              <input
                type="radio"
                name="head-table-type"
                checked={data.headTableType === 'sweetheart_table'}
                onChange={() => onChange({ headTableType: 'sweetheart_table', headTablePartySize: null })}
              />
              <span className="font-body text-sm text-ink-600">Sweetheart table (just the two of you)</span>
            </label>
          </div>
        </>
      )}

      <button
        onClick={onNext}
        disabled={!canContinue}
        className="mt-8 w-full rounded bg-evergreen-600 px-4 py-3 font-body font-medium text-ivory-50 hover:bg-evergreen-700 disabled:opacity-40"
      >
        Continue
      </button>
    </div>
  );
}
